import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Eye, EyeOff, Loader2, ShieldAlert, Lock,
    Activity, Server, Users, Globe, Fingerprint,
    AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

// ── Keyframe styles ─────────────────────────────────────────────────
const styles = `
@import url('https://fonts.googleapis.com/css2?family=DM+Mono:ital,wght@0,300;0,400;0,500;1,400&family=Syne:wght@400;500;600;700;800&display=swap');

@keyframes scanline {
  0%   { transform: translateY(-100%); opacity: 0; }
  10%  { opacity: 0.08; }
  90%  { opacity: 0.08; }
  100% { transform: translateY(100vh); opacity: 0; }
}

@keyframes pulse-ring {
  0%   { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(251, 191, 36, 0.4); }
  70%  { transform: scale(1);    box-shadow: 0 0 0 14px rgba(251, 191, 36, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(251, 191, 36, 0); }
}

@keyframes fadeSlideUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
}

@keyframes grid-shift {
  0%   { transform: translateY(0); }
  100% { transform: translateY(40px); }
}

@keyframes stat-in {
  from { opacity: 0; transform: translateX(-12px); }
  to   { opacity: 1; transform: translateX(0); }
}

.admin-font-display { font-family: 'Syne', sans-serif; }
.admin-font-mono    { font-family: 'DM Mono', monospace; }

.scanline {
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 120px;
  background: linear-gradient(to bottom, transparent, rgba(251,191,36,0.06), transparent);
  animation: scanline 6s linear infinite;
  pointer-events: none;
  z-index: 5;
}

.grid-bg {
  background-image:
    linear-gradient(rgba(251,191,36,0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(251,191,36,0.04) 1px, transparent 1px);
  background-size: 40px 40px;
  animation: grid-shift 8s linear infinite;
}

.form-fade { animation: fadeSlideUp 0.6s ease forwards; }
.form-fade-1 { animation: fadeSlideUp 0.6s 0.1s ease both; }
.form-fade-2 { animation: fadeSlideUp 0.6s 0.2s ease both; }
.form-fade-3 { animation: fadeSlideUp 0.6s 0.3s ease both; }
.form-fade-4 { animation: fadeSlideUp 0.6s 0.4s ease both; }
.form-fade-5 { animation: fadeSlideUp 0.6s 0.5s ease both; }

.cursor-blink { animation: blink 1s step-end infinite; }

.stat-card { animation: stat-in 0.5s ease both; }
.stat-card:nth-child(1) { animation-delay: 0.2s; }
.stat-card:nth-child(2) { animation-delay: 0.35s; }
.stat-card:nth-child(3) { animation-delay: 0.5s; }

.input-admin {
  background: rgba(255,255,255,0.03);
  border: 1.5px solid rgba(251,191,36,0.15);
  color: #f3f4f6;
  border-radius: 10px;
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.input-admin:focus {
  outline: none;
  border-color: rgba(251,191,36,0.6);
  background: rgba(251,191,36,0.04);
  box-shadow: 0 0 0 3px rgba(251,191,36,0.08);
}
.input-admin::placeholder { color: rgba(156,163,175,0.5); }

.submit-btn {
  background: linear-gradient(135deg, #d97706, #f59e0b, #fbbf24);
  background-size: 200% 200%;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.submit-btn:not(:disabled):hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 28px rgba(251,191,36,0.35);
  background-position: right center;
}
.submit-btn:not(:disabled):active {
  transform: translateY(0);
}
.submit-btn::after {
  content: '';
  position: absolute;
  top: 0; left: -100%;
  width: 100%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent);
  transition: left 0.45s ease;
}
.submit-btn:not(:disabled):hover::after { left: 100%; }

.shield-pulse { animation: pulse-ring 2.5s ease-out infinite; }

.access-badge {
  background: rgba(239,68,68,0.1);
  border: 1px solid rgba(239,68,68,0.3);
  color: #f87171;
}
`;

// ── Live system stat ─────────────────────────────────────────────────
const StatItem = ({ icon: Icon, label, value, color }) => (
    <div className="stat-card flex items-center gap-3 px-4 py-3 rounded-xl"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}15` }}>
            <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div>
            <p className="admin-font-mono text-[10px] leading-none mb-1" style={{ color: 'rgba(156,163,175,0.7)' }}>{label}</p>
            <p className="admin-font-mono text-sm font-medium" style={{ color: '#f3f4f6' }}>{value}</p>
        </div>
    </div>
);

// ═══════════════════════════════════════════════════════════════════
const AdminLogin = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading]           = useState(false);
    const [formData, setFormData]         = useState({ username: '', password: '' });
    const [currentTime, setCurrentTime]   = useState('');

    // Live clock
    useEffect(() => {
        const tick = () => {
            const now = new Date();
            setCurrentTime(
                now.toUTCString().replace('GMT', 'UTC')
            );
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, []);

    // Inject styles
    useEffect(() => {
        const el = document.createElement('style');
        el.id = 'admin-login-styles';
        el.innerText = styles;
        document.head.appendChild(el);
        return () => document.getElementById('admin-login-styles')?.remove();
    }, []);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            /* ── REAL API (uncomment when ready) ──────────────────
            const res = await axios.post(`${BACKEND_URL}/api/admin/auth/login`, {
                username: formData.username,
                password: formData.password,
            });
            if (!res.data.success) throw new Error(res.data.detail || 'Login failed');
            // store token, set auth, etc.
            ──────────────────────────────────────────────────────── */

            // ── MOCK ──
            await new Promise(r => setTimeout(r, 1200));

            // ✅ Set the super admin flag in localStorage so Sidebar can read it
            localStorage.setItem('isSuperAdmin', 'true');

            toast.success('Super Admin access granted');
            navigate('/admin');
            // ──────────

        } catch (err) {
            toast.error(err?.response?.data?.detail || err?.message || 'Authentication failed');
        }

        setLoading(false);
    };

    return (
        <div className="admin-font-display flex min-h-screen w-full"
            style={{ backgroundColor: '#060a06' }}>

            <style>{styles}</style>

            {/* ════════ LEFT PANEL ════════ */}
            <div className="hidden lg:flex relative lg:w-[55%] flex-col justify-between p-12 overflow-hidden"
                style={{ background: 'linear-gradient(145deg, #0a0f06 0%, #0d1209 40%, #07100a 100%)' }}>

                {/* Grid background */}
                <div className="grid-bg absolute inset-0 z-0" />

                {/* Scanline sweep */}
                <div className="scanline" />

                {/* Amber glow blob */}
                <div className="absolute top-1/4 left-1/3 w-96 h-96 rounded-full pointer-events-none z-0"
                    style={{
                        background: 'radial-gradient(circle, rgba(251,191,36,0.07) 0%, transparent 70%)',
                        filter: 'blur(40px)'
                    }} />

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full">

                    {/* Logo + restricted badge */}
                    <div className="flex items-center justify-between mb-12">
                        <img
                            src="https://customer-assets.emergentagent.com/job_c083c188-1797-4d1e-a073-4159fcae87e4/artifacts/z4ndtxat_shipez-logo-new.webp"
                            alt="Shipez Logo"
                            className="h-[60px] w-auto"
                        />
                        <div className="access-badge flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs admin-font-mono">
                            <AlertTriangle className="w-3.5 h-3.5" />
                            RESTRICTED ACCESS
                        </div>
                    </div>

                    {/* Headline */}
                    <div className="mb-10">
                        <div className="flex items-center gap-2 mb-4">
                            <div className="h-px flex-1" style={{ background: 'linear-gradient(to right, rgba(251,191,36,0.5), transparent)' }} />
                            <span className="admin-font-mono text-[11px] tracking-[0.2em]" style={{ color: 'rgba(251,191,36,0.6)' }}>
                                SUPER ADMIN PORTAL
                            </span>
                        </div>
                        <h1 className="text-4xl xl:text-5xl font-bold leading-tight mb-4"
                            style={{ color: '#f9fafb', letterSpacing: '-0.02em' }}>
                            System Control<br />
                            <span style={{
                                backgroundImage: 'linear-gradient(90deg, #fbbf24, #f59e0b, #d97706)',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>
                                Center
                            </span>
                        </h1>
                        <p className="text-base leading-relaxed max-w-sm" style={{ color: 'rgba(156,163,175,0.8)' }}>
                            Elevated access with full platform oversight, tenant management, and system configuration.
                        </p>
                    </div>

                    {/* Live system stats */}
                    <div className="grid grid-cols-1 gap-3 mb-10 max-w-sm">
                        <StatItem icon={Users}    label="ACTIVE TENANTS"   value="48 companies"     color="#fbbf24" />
                        <StatItem icon={Server}   label="SYSTEM STATUS"    value="All services up"  color="#34d399" />
                        <StatItem icon={Globe}    label="PLATFORM REGION"  value="US-East / Global" color="#60a5fa" />
                    </div>

                    {/* Admin capabilities */}
                    <div className="mt-auto">
                        <p className="admin-font-mono text-[11px] tracking-widest mb-4"
                            style={{ color: 'rgba(251,191,36,0.5)' }}>
                            ADMIN CAPABILITIES
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {['Tenant Management', 'Module Control', 'Billing Override',
                              'User Impersonation', 'Audit Logs', 'System Config'].map(cap => (
                                <span key={cap}
                                    className="admin-font-mono text-[11px] px-3 py-1.5 rounded-md"
                                    style={{
                                        background: 'rgba(251,191,36,0.07)',
                                        border: '1px solid rgba(251,191,36,0.15)',
                                        color: 'rgba(251,191,36,0.8)'
                                    }}>
                                    {cap}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ════════ RIGHT PANEL — Form ════════ */}
            <div className="w-full lg:w-[45%] flex items-center justify-center p-6 sm:p-8 relative"
                style={{ background: 'linear-gradient(160deg, #0a0f06 0%, #060a06 100%)' }}>

                {/* Subtle noise texture overlay */}
                <div className="absolute inset-0 opacity-[0.015] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                        backgroundSize: '200px'
                    }} />

                {/* Mobile logo */}
                <div className="absolute top-5 left-5 lg:hidden">
                    <img
                        src="https://customer-assets.emergentagent.com/job_c083c188-1797-4d1e-a073-4159fcae87e4/artifacts/z4ndtxat_shipez-logo-new.webp"
                        alt="Shipez Logo"
                        className="h-9 w-auto"
                    />
                </div>

                <div className="w-full max-w-[420px] relative z-10">

                    {/* Shield icon */}
                    <div className="form-fade flex justify-center mb-8">
                        <div className="shield-pulse w-16 h-16 rounded-2xl flex items-center justify-center"
                            style={{
                                background: 'rgba(251,191,36,0.1)',
                                border: '1.5px solid rgba(251,191,36,0.3)'
                            }}>
                            <ShieldAlert className="w-8 h-8" style={{ color: '#fbbf24' }} />
                        </div>
                    </div>

                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="form-fade-1">
                            <h2 className="text-3xl sm:text-4xl font-bold mb-2"
                                style={{
                                    backgroundImage: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
                                    WebkitBackgroundClip: 'text',
                                    backgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    letterSpacing: '-0.02em'
                                }}>
                                Admin Access
                            </h2>
                        </div>
                        <div className="form-fade-2">
                            <p className="text-sm admin-font-mono" style={{ color: 'rgba(156,163,175,0.7)' }}>
                                Authenticate with super admin credentials
                            </p>
                        </div>
                    </div>

                    {/* Live timestamp */}
                    <div className="form-fade-2 flex items-center gap-2 px-3 py-2 rounded-lg mb-6"
                        style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.12)' }}>
                        <Activity className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#fbbf24' }} />
                        <span className="admin-font-mono text-[11px] truncate" style={{ color: 'rgba(251,191,36,0.7)' }}>
                            {currentTime || 'Syncing…'}
                        </span>
                        <span className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0 cursor-blink"
                            style={{ background: '#34d399' }} />
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">

                        {/* Username */}
                        <div className="form-fade-2 flex flex-col gap-1.5">
                            <label className="text-sm font-semibold admin-font-mono tracking-wide"
                                style={{ color: 'rgba(251,191,36,0.8)' }}>
                                Admin Username
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder="superadmin"
                                    className="input-admin w-full py-3 sm:py-4 px-4 pl-10 text-sm admin-font-mono"
                                    required
                                />
                                <Fingerprint className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                                    style={{ color: 'rgba(251,191,36,0.4)' }} />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="form-fade-3 flex flex-col gap-1.5">
                            <label className="text-sm font-semibold admin-font-mono tracking-wide"
                                style={{ color: 'rgba(251,191,36,0.8)' }}>
                                Admin Password
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    placeholder="••••••••••••"
                                    className="input-admin w-full py-3 sm:py-4 px-4 pl-10 pr-11 text-sm admin-font-mono"
                                    required
                                />
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                                    style={{ color: 'rgba(251,191,36,0.4)' }} />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(p => !p)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-lg transition-colors"
                                    style={{ color: 'rgba(156,163,175,0.5)' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#fbbf24'}
                                    onMouseLeave={e => e.currentTarget.style.color = 'rgba(156,163,175,0.5)'}
                                    tabIndex={-1}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        {/* Warning notice */}
                        <div className="form-fade-4 flex items-start gap-2.5 px-3.5 py-3 rounded-xl"
                            style={{
                                background: 'rgba(239,68,68,0.06)',
                                border: '1px solid rgba(239,68,68,0.2)'
                            }}>
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#f87171' }} />
                            <p className="text-xs admin-font-mono leading-relaxed" style={{ color: 'rgba(248,113,113,0.85)' }}>
                                All admin actions are logged and audited. Unauthorized access attempts will be reported.
                            </p>
                        </div>

                        {/* Submit */}
                        <div className="form-fade-5">
                            <button
                                type="submit"
                                disabled={loading}
                                className="submit-btn w-full py-3.5 sm:py-4 rounded-xl font-bold text-sm tracking-wide text-slate-900 disabled:opacity-60 disabled:cursor-not-allowed admin-font-display"
                            >
                                {loading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Authenticating…
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center gap-2">
                                        <ShieldAlert className="w-4 h-4" />
                                        Authenticate as Admin
                                    </span>
                                )}
                            </button>
                        </div>
                    </form>

                    {/* Back to regular login */}
                    <div className="form-fade-5 mt-6 pt-5 text-center admin-font-mono"
                        style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(156,163,175,0.6)' }}>
                        <span className="text-xs">Not an admin?</span>
                        <a
                            href="/login"
                            className="ml-2 text-xs font-semibold transition-colors"
                            style={{ color: '#84cc16' }}
                            onMouseEnter={e => e.currentTarget.style.color = '#a3e635'}
                            onMouseLeave={e => e.currentTarget.style.color = '#84cc16'}
                        >
                            Back to regular login →
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminLogin;